"use client"

import { t } from "@/languages/i18n"
import { PostData } from "@/lib/config/types"
import { postCampaignRegistration } from "@/services/campaign-subscriber-service"
import { getCampaignById } from "@/services/get-campaign"
import { DatePicker } from "antd"
import csCZ from "antd/locale/cs_CZ"
import enUS from "antd/locale/en_US"
import viVN from "antd/locale/vi_VN"
import dayjs from "dayjs"
import { useEffect, useMemo, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { RecommendProduct } from "./RecommendProduct"

interface CampaignProps {
  postData: PostData
}

interface CampaignForm {
  branch?: string
  email?: string
  fullname?: string
  invoiceNumber?: string
  phone?: string
  purchasingDate?: string
  policy?: boolean
}

interface AlertProps {
  message: string
  type?: "success" | "error" | undefined
  title: string
}

/**
 * Strapi lưu dateFrom/dateTo dạng ISO date-time (`2025-08-01T22:00:00.000Z`),
 * còn min/max của <input type="date"> chỉ chấp nhận `YYYY-MM-DD` — sai định dạng
 * thì trình duyệt bỏ qua thuộc tính và cho chọn ngày tự do.
 * Cắt theo giờ địa phương để ra đúng ngày admin đã chọn trong trang quản trị.
 */
const toDateInputValue = (value?: string): string | undefined => {
  if (!value) return undefined
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined

  const pad = (n: number) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const INITIAL_FORM_DATA: CampaignForm = {
  branch: "",
  email: "",
  fullname: "",
  invoiceNumber: "",
  phone: "",
  purchasingDate: "",
}

export const Campaign = ({ postData }: CampaignProps) => {
  // Extract campaign data from postData
  const campaign = postData?.attributes?.campaign
  const campaignData = (campaign?.data ?? null) as any
  const campaignId = campaignData?.id as number | undefined
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY?.trim()
  const recaptchaEnabled = !!recaptchaSiteKey
  const recaptchaValueRef = useRef<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA | null>(null)
  const [notification, setNotification] = useState<AlertProps>({ message: "", type: undefined, title: "" })
  const [disabledCheckbox, setDisabledCheckbox] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [formData, setFormData] = useState<CampaignForm>(INITIAL_FORM_DATA)
  const [errors, setErrors] = useState<Partial<Record<keyof CampaignForm, string>>>({})
  const [campaignDetail, setCampaignDetail] = useState<any | null>(null)

  useEffect(() => {
    let alive = true
    const run = async () => {
      if (!campaignId) return
      try {
        const res = await getCampaignById(campaignId, postData?.attributes?.locale)
        if (!alive) return
        setCampaignDetail(res?.data || null)
      } catch {
        // Fallback: keep using embedded relation from postData (may not include tamdaStores)
        if (!alive) return
        setCampaignDetail(null)
      }
    }
    run()
    return () => {
      alive = false
    }
  }, [campaignId, postData?.attributes?.locale])
  

  const eans = useMemo(() => campaignData?.attributes?.relatedProducts?.split("\n") ?? [], [
    campaignData?.attributes?.relatedProducts,
  ])

  const selectData = useMemo(() => {
    const tamdaStoresData =
      campaignDetail?.attributes?.tamdaStores?.data ?? campaignData?.attributes?.tamdaStores?.data ?? []

    const stores = (tamdaStoresData.map((store: any) => store?.attributes?.name).filter(Boolean) as string[]) ?? []

    const isExpress = !!(campaignDetail?.attributes?.tamdaExpressCampaign ?? campaignData?.attributes?.tamdaExpressCampaign)
    if (isExpress) {
      stores.push("Tamda Express")
    }
    return stores
  }, [campaignDetail, campaignData?.attributes?.tamdaExpressCampaign, campaignData?.attributes?.tamdaStores])

  // Khoảng thời gian hợp lệ của chiến dịch — chỉ cho chọn ngày mua trong khoảng này
  const dateRange = useMemo(() => {
    const attrs = campaignDetail?.attributes ?? campaignData?.attributes
    return {
      from: toDateInputValue(attrs?.dateFrom),
      to: toDateInputValue(attrs?.dateTo),
    }
  }, [campaignDetail, campaignData?.attributes])

  // Panel lịch hiển thị theo ngôn ngữ của bài viết (tên tháng, thứ trong tuần)
  const pickerLocale = useMemo(() => {
    const locales: Record<string, typeof viVN> = { vi: viVN, en: enUS, cs: csCZ }
    return (locales[postData?.attributes?.locale || "vi"] ?? viVN).DatePicker
  }, [postData?.attributes?.locale])

  const validateForm = () => {
    const newErrors: Partial<Record<keyof CampaignForm, string>> = {}

    if (!formData.fullname) newErrors.fullname = "Không được để trống"
    if (!formData.phone) newErrors.phone = "Không được để trống"
    if (!formData.invoiceNumber) newErrors.invoiceNumber = "Không được để trống"
    if (!formData.branch) newErrors.branch = "Không được để trống"
    if (!formData.purchasingDate) {
      newErrors.purchasingDate = "Không được để trống"
    } else if (
      (dateRange.from && formData.purchasingDate < dateRange.from) ||
      (dateRange.to && formData.purchasingDate > dateRange.to)
    ) {
      // min/max chỉ chặn được thao tác chọn, người dùng vẫn có thể gõ tay ngày ngoài khoảng
      newErrors.purchasingDate = "Ngày mua hàng phải nằm trong thời gian diễn ra chương trình"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field: keyof CampaignForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const handleRecaptchaChange = (token: string | null) => {
    recaptchaValueRef.current = token
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm() || !disabledCheckbox) {
      return
    }

    if (recaptchaEnabled && !recaptchaValueRef.current) {
      setNotification({
        title: t("Captcha error"),
        message: t("Please complete captcha before submitting."),
        type: "error",
      })
      return
    }

    setLoading(true)
    try {
      // Lưu bản ghi `campaign-subscriber` trong Strapi rồi mới gửi email (xử lý ở route handler)
      await postCampaignRegistration({
        fullname: formData.fullname,
        phone: formData.phone,
        email: formData.email,
        invoiceNumber: formData.invoiceNumber,
        branch: formData.branch,
        purchasingDate: formData.purchasingDate,
        campaign: campaignId,
        campaignName: campaignDetail?.attributes?.name ?? campaignData?.attributes?.name,
      })

      // Chỉ báo thành công khi đăng ký đã được lưu
      setNotification({
        title: t("Thank you!"),
        message: t("Thank you, we will contact you soon."),
        type: "success",
      })

      setFormData(INITIAL_FORM_DATA)
      setErrors({})
      setDisabledCheckbox(false)
      recaptchaValueRef.current = null
      recaptchaRef.current?.reset()
    } catch (ex) {
      console.error("Failed to submit campaign registration", ex)
      setNotification({
        title: t("Error!"),
        message: t("Something went wrong, please try again."),
        type: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  // Đặt sau toàn bộ hook để số lượng hook không đổi giữa các lần render
  if (!campaignData) {
    return null
  }

  return (
    <div className="py-6">
      <RecommendProduct eans={eans} />

      <h2 className="text-center uppercase text-2xl font-bold mt-8 mb-6">{t("Register now")}</h2>

      <form onSubmit={onSubmit} className="mx-auto">
        <div className="grid grid-cols-1 gap-4">
          {/* Full Name */}
          <div className="flex flex-col">
            <label className="text-base leading-[24.8px] text-[#212529] font-medium mb-1">
              {t("Full name")} <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formData.fullname}
              onChange={e => handleInputChange("fullname", e.target.value)}
              className={`border rounded px-3 py-2 h-12 focus:outline-none focus:border-green-500 focus:border-[0.5px] ${errors.fullname ? "border-red-500" : "border-gray-300"
                }`}
            />
            {errors.fullname && <span className="text-red-500 text-xs mt-1">{errors.fullname}</span>}
          </div>

          {/* Phone */}
          <div className="flex flex-col">
            <label className="text-base leading-[24.8px] text-[#212529] font-medium mb-1">
              {t("Phone number")} <span className="text-red-600">*</span>
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={e => handleInputChange("phone", e.target.value)}
              className={`border rounded px-3 py-2 h-12 focus:outline-none focus:border-[#18AF4E] focus:border-[0.5px] ${errors.phone ? "border-red-500" : "border-gray-300"
                }`}
            />
            {errors.phone && <span className="text-red-500 text-xs mt-1">{errors.phone}</span>}
          </div>

          {/* Email */}
          <div className="flex flex-col">
            <label className="text-base leading-[24.8px] text-[#212529] font-medium mb-1">E-mail</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => handleInputChange("email", e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 h-12 focus:outline-none focus:border-[#18AF4E] focus:border-[0.5px]"
            />
          </div>

          {/* Invoice Number */}
          <div className="flex flex-col">
            <label className="text-base leading-[24.8px] text-[#212529] font-medium mb-1">
              {t("Invoice number")} <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formData.invoiceNumber}
              onChange={e => handleInputChange("invoiceNumber", e.target.value)}
              className={`border rounded px-3 py-2 h-12 focus:outline-none focus:border-[#18AF4E] focus:border-[0.5px] ${errors.invoiceNumber ? "border-red-500" : "border-gray-300"
                }`}
            />
            {errors.invoiceNumber && <span className="text-red-500 text-xs mt-1">{errors.invoiceNumber}</span>}
          </div>

          {/* Branch */}
          <div className="flex flex-col">
            <label className="text-base leading-[24.8px] text-[#212529] font-medium mb-1">
              {t("Shopping place")} <span className="text-red-600">*</span>
            </label>
            <select
              value={formData.branch}
              onChange={e => handleInputChange("branch", e.target.value)}
              className={`border rounded px-3 py-2 h-12 focus:outline-none focus:border-[#18AF4E] focus:border-[0.5px] ${errors.branch ? "border-red-500" : "border-gray-300"
                }`}
            >
              {/* Placeholder rỗng: không hiện text mặc định và không nằm trong danh sách chọn */}
              <option value="" hidden />
              {selectData.map((store, idx) => (
                <option key={idx} value={store}>
                  {store}
                </option>
              ))}
            </select>
            {errors.branch && <span className="text-red-500 text-xs mt-1">{errors.branch}</span>}
          </div>

          {/* Purchasing Date */}
          <div className="flex flex-col">
            <label className="text-base leading-[24.8px] text-[#212529] font-medium mb-1">
              {t("Purchasing date")} <span className="text-red-600">*</span>
            </label>
            <DatePicker
              // Native <input type="date"> hiển thị theo locale của trình duyệt (dễ ra mm/dd/yyyy),
              // nên dùng DatePicker để ép format dd/mm/yyyy giống bản đang chạy production.
              format="DD/MM/YYYY"
              locale={pickerLocale}
              placeholder=""
              value={formData.purchasingDate ? dayjs(formData.purchasingDate) : null}
              onChange={date => handleInputChange("purchasingDate", date ? date.format("YYYY-MM-DD") : "")}
              disabledDate={current =>
                !!(
                  (dateRange.from && current.isBefore(dayjs(dateRange.from), "day")) ||
                  (dateRange.to && current.isAfter(dayjs(dateRange.to), "day"))
                )
              }
              status={errors.purchasingDate ? "error" : undefined}
              className="h-12 w-full rounded"
            />
            {errors.purchasingDate && <span className="text-red-500 text-xs mt-1">{errors.purchasingDate}</span>}
          </div>
        </div>

        {/* Checkbox */}
        <div className="flex items-center mt-4">
          <input
            type="checkbox"
            checked={disabledCheckbox}
            onChange={e => setDisabledCheckbox(e.target.checked)}
            className="mt-1 mr-2 w-[18px] h-[18px] rounded-xl border-2 border-[#EBEBEB] bg-white  relative flex items-center justify-center transition"
            id="policy-checkbox"
          />
          <label htmlFor="policy-checkbox" className="text-sm">
            {t("I agree with the terms and conditions of the promotion program")}
          </label>

        </div>
        {recaptchaEnabled ? (
          <div className="mt-3">
            <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaSiteKey!} onChange={handleRecaptchaChange} />
          </div>
        ) : (
          <p className="mt-3 text-sm text-red-600">
            {t("Missing reCAPTCHA v2 key (NEXT_PRIVATE_RECAPTCHA_SITE_KEY). Please configure to show captcha.")}
          </p>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!disabledCheckbox || loading}
          className={`w-full h-16 mt-4 rounded font-semibold text-white transition-colors ${!disabledCheckbox || loading ? "!bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
            }`}
        >
          {loading ? t("Loading...") : t("Register now")}
        </button>

        {/* Notification */}
        {notification.type && (
          <div
            className={`mt-4 p-4 rounded ${notification.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}
          >
            <h3 className="font-bold">{notification.title}</h3>
            <p>{notification.message}</p>
          </div>
        )}
      </form>
    </div>
  )
}
